import {
  Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box,
  CircularProgress,
  CircularProgressLabel, Code, HStack, Link, Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent, ModalHeader,
  ModalOverlay,
  Text, useToast, VStack
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

const HistoryModal = (props) => {
  const {
    historicalData,
    latestRerport,
    deviceType,
    url,
    label,
    scoreColor,
    AuditIcon,
    Bar,
    isOpen,
    onClose,
  } = props
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false)
  const { t } = useTranslation()
  const toast = useToast()

  const latestScore = (latestRerport?.performanceScore?.[deviceType]) || null;
  const displayScore = latestScore ? Math.round(latestScore * 100) : "-";
  let latestScoreDate;
  if (latestScore) latestScoreDate = `${moment(latestRerport?.date).format("Do MMM, YY")}`;

  const getColor = (score) => {
    const color = scoreColor(score);
    return color.split(".")[0];
  }

  const auditTypes = {
    "uses-optimized-images": "Image Optimization",
    "uses-responsive-images": "Image Responsiveness",
    "modern-image-formats": "Image Format Modernization",
    "modern-video-formats": "Video Format Modernization",
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent width="800px" maxWidth="800px">
        <ModalHeader>
          Historical trends
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            {latestScore !== null && (
              <>
                <CircularProgress trackColor={`${getColor(latestScore)}.100`} capIsRound={true} value={latestScore * 100} color={`${getColor(latestScore)}.400`} size='150px' thickness='6px'>
                  <CircularProgressLabel color={`${getColor(latestScore)}.700`}>
                    <Box
                      bg={`${getColor(latestScore)}.100`}
                      height={"118px"}
                      width={"118px"}
                      margin={"0 auto"}
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                      position={"relative"}
                      borderRadius={"full"}
                      fontSize={"32px"}
                      fontWeight={"bold"}>
                      {Math.round(latestScore * 100)}
                    </Box>
                  </CircularProgressLabel>
                </CircularProgress>

                <HStack gap={8}>
                  <Box display={"flex"} justifyContent="center" alignItems="center" gap={2}>
                    <AuditIcon score={0.1} />
                    <Box>0–49</Box>
                  </Box>
                  <Box display={"flex"} justifyContent="center" alignItems="center" gap={2}>
                    <AuditIcon score={0.5} />
                    <Box>50–89</Box>
                  </Box>
                  <Box display={"flex"} justifyContent="center" alignItems="center" gap={2}>
                    <AuditIcon score={0.9} />
                    <Box>90–100</Box>
                  </Box>
                </HStack>

                <Text>
                  Performance score as on {latestScoreDate}
                </Text>

                <Text textTransform={"capitalize"}>
                  {label} - {deviceType}
                </Text>
              </>
            )}

            <Accordion w={"full"} allowMultiple={true} defaultIndex={Object.keys(auditTypes).map((_, index) => index)}>
              {Object.keys(auditTypes).map((auditType, index) => (
                <AccordionItem>
                  <h2>
                    <AccordionButton p={4}>
                      <Box flex='1' textAlign='left'>
                        {auditTypes[auditType]}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <VStack spacing={4}>
                      <ExtraInfo audit={{ id: auditType }} />
                      <Box w={"full"}>
                        <HStack spacing={"2px"}>
                          {historicalData.map((data, index) => (
                            <Bar
                              key={index}
                              deviceType={deviceType}
                              score={data?.audits?.[deviceType]?.[auditType]}
                              date={data?.date}
                              taskId={data?.taskId}
                            />
                          ))}
                        </HStack>
                      </Box>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const getURLInfo = (url) => {
  // Get hostname
  const hostname = new URL(url).hostname;

  const path = new URL(url).pathname;

  return {
    hostname: hostname,
    displayPath: getTruncatedUrl(path)
  }
}

const getTruncatedUrl = (url, maxLength = 50) => {
  if (url.length <= maxLength) {
    return url; // Return the original URL if it's within the maximum length
  }

  const ellipsis = '...';
  const keepLength = Math.floor((maxLength - ellipsis.length) / 2);

  const start = url.substring(0, keepLength); // Get the first part of the URL
  const end = url.substring(url.length - keepLength); // Get the last part of the URL

  return `${start}${ellipsis}${end}`;
}

const ExtraInfo = ({ audit }) => {

  switch (audit.id) {
    case "uses-responsive-images":
      const exampleURL = audit?.details?.items[0]?.url ?? "https://ik.imagekit.io/demo/sample.jpg";
      const pathname = getURLInfo(exampleURL).displayPath
      return <VStack mt={4} spacing={4} align={"flex-start"}>
        <Text>
          With ImageKit, resizing is as simple as changing the URL. You can resize images on the fly by specifying the width and height in the URL. For example, to resize an image to 200x200 pixels, you can add <Code>?tr=w-200,h-200</Code> to the image URL.
        </Text>

        <Box>
          <Code>{pathname}?tr=w-200,h-200</Code>
        </Box>

        <Text>
          Apart from resizing and cropping, you can also leverage AI-powered smart cropping to generate perfect thumbnails for your images. <Link href="https://imagekit.io/docs/transformations" target='_blank'>Learn more</Link>.
        </Text>
      </VStack>
    case "modern-image-formats":
      return <VStack mt={4} spacing={4} align={"flex-start"}>
        <Text>
          ImageKit automatically converts images to WebP or AVIF format for browsers that support it. This can reduce the size of images by more than 30% without any loss in quality.
        </Text>
        <Text>
          You can also force the conversion of images to a specific format by adding <Code>?tr=f-webp</Code> to the image URL. <Link href="https://imagekit.io/docs/image-optimization#automatic-format-conversion" target='_blank'>Learn more</Link>.
        </Text>
      </VStack>
    case "uses-optimized-images":
      return <VStack mt={4} spacing={4} align={"flex-start"}>
        <Text>
          ImageKit automatically compresses images to reduce their size without any loss in quality. You don't have to change URL or worry about the compression during image upload.
        </Text>
        <Text>
          You can also control the quality of images by adding <Code>?tr=q-80</Code> to the image URL. <Link href="https://imagekit.io/docs/image-optimization#automatic-image-compression" target='_blank'>Learn more</Link>.
        </Text>
      </VStack>
    case "efficient-animated-content":
      return <VStack mt={4} spacing={4} align={"flex-start"}>
        <Text>
          ImageKit can convert GIFs to videos, which can reduce the size of animated content by up to 90%. This can significantly improve the user experience and reduce data transfer costs.
        </Text>
        <Text>
          To convert a GIF image to a video file, add <Code>/ik-gif-video.mp4</Code> after the GIF resource URL.
        </Text>
      </VStack>
    case "modern-video-formats":
      return <VStack mt={4} spacing={4} align={"flex-start"}>
        <Text>
          ImageKit automatically converts videos to WebM format with VP9 or AV1 codec for browsers that support it. This optimization works without changing the URL of the video.
        </Text>
        <Text>
          You can also force the conversion of videos to a specific format by adding <Code>?tr=f-webm</Code> to the video URL. <Link href="https://imagekit.io/docs/video-optimization" target='_blank'>Learn more</Link>.
        </Text>
      </VStack>
  }
  return null
}

export default HistoryModal
